import { useState, useRef, useEffect, useCallback } from "react";
import { MainLayout } from "../components/layout/MainLayout";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Send, Bot, User, Loader2, Sparkles, Headphones, BarChart3, Wrench, Shield, Wifi, Globe, CreditCard, Activity } from "lucide-react";
import { useSearchParams } from "react-router-dom";

const EXEC_PROMPTS = [
  "Como foi o mes ate agora?",
  "Qual produto mais vendido?",
  "Como estao as vendas esta semana?",
  "Taxa de cancelamento esta alta?",
  "Qual dia vende mais?",
  "Sugestoes para aumentar o ticket medio?",
];

const SUPPORT_THINKING_STEPS = [
  "Analisando sistema do restaurante...",
  "Verificando WhatsApp e pedidos...",
  "Consultando configurações...",
  "Preparando diagnóstico...",
  "Elaborando resposta...",
];

const SUPPORT_PROMPTS = [
  "Faca um diagnostico completo do meu restaurante",
  "Meu dominio nao esta funcionando",
  "O WhatsApp nao conecta",
  "Como configuro meu menu?",
  "Pagamentos nao estao funcionando",
  "Como ativo delivery?",
  "As imagens nao aparecem",
  "A impressora nao funciona",
  "Como configuro pagamentos?",
];

export default function AIAssistantPage() {
  const { authAxios } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [searchParams, setSearchParams] = useSearchParams();
  const initialMode = searchParams.get("mode") === "help" ? "support" : "executive";
  const [mode, setMode] = useState(initialMode);
  const [messages, setMessages] = useState([
    { role: "assistant", content: mode === "support"
      ? "Ola! Sou o **Assistente Tecnico Inteligente** do Kaiso System. Tenho acesso ao diagnostico em tempo real do seu restaurante — posso verificar DNS, WhatsApp, pagamentos, configuracoes e muito mais. Pergunte qualquer coisa ou peca um **diagnostico completo**!"
      : "Ola! Sou o **Consultor Executivo IA** do seu restaurante. Tenho acesso aos seus dados reais - vendas, produtos, clientes. Pergunte qualquer coisa sobre o seu negocio!" },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [diagnostics, setDiagnostics] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [thinkingStep, setThinkingStep] = useState(0);
  const thinkingTimerRef = useRef(null);
  const messagesEndRef = useRef(null);


  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Load diagnostics when in support mode
  useEffect(() => {
    if (mode === "support" && !diagnostics) {
      setDiagLoading(true);
      authAxiosRef.current.get("/tech-assistant/diagnostics").then(res => {
        setDiagnostics(res.data);
      }).catch(() => {}).finally(() => setDiagLoading(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, diagnostics]);

  const switchMode = (newMode) => {
    setMode(newMode);
    setSearchParams(newMode === "support" ? { mode: "help" } : {});
    setMessages([{
      role: "assistant",
      content: newMode === "support"
        ? "Ola! Sou o **Assistente Tecnico Inteligente** do Kaiso System. Tenho acesso ao diagnostico em tempo real do seu restaurante — posso verificar DNS, WhatsApp, pagamentos, configuracoes e muito mais. Pergunte qualquer coisa ou peca um **diagnostico completo**!"
        : "Ola! Sou o **Consultor Executivo IA** do seu restaurante. Tenho acesso aos seus dados reais - vendas, produtos, clientes. Pergunte qualquer coisa sobre o seu negocio!"
    }]);
  };

  const sendMessage = useCallback(async (text) => {
    const userMsg = text || input.trim();
    if (!userMsg || loading) return;
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMsg }]);
    setLoading(true);

    // Progress steps for support mode
    if (mode === "support") {
      setThinkingStep(0);
      let step = 0;
      thinkingTimerRef.current = setInterval(() => {
        step = (step + 1) % SUPPORT_THINKING_STEPS.length;
        setThinkingStep(step);
      }, 1500);
    }

    try {
      const res = await authAxiosRef.current.post("/ai/chat", { message: userMsg, context: mode });
      setMessages(prev => [...prev, { role: "assistant", content: res.data.response }]);
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      const isKeyErr = detail.includes("configurad") || detail.includes("provider");
      const errContent = isKeyErr
        ? "⚠️ **Serviço de IA não configurado.** Verifique as chaves API no servidor."
        : "❌ Erro ao processar. Tente novamente em alguns segundos.";
      if (isKeyErr) toast.error("Chave de IA não configurada — verifique ANTHROPIC_API_KEY ou OPENAI_API_KEY");
      setMessages(prev => [...prev, { role: "assistant", content: errContent }]);
    } finally {
      if (thinkingTimerRef.current) {
        clearInterval(thinkingTimerRef.current);
        thinkingTimerRef.current = null;
      }
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input, loading, mode]);

  const isSupport = mode === "support";
  const QUICK_PROMPTS = isSupport ? SUPPORT_PROMPTS : EXEC_PROMPTS;

  return (
    <MainLayout>
      <div className="flex flex-col h-[calc(100vh-2rem)] max-w-4xl mx-auto p-4" data-testid="ai-assistant-page">
        {/* Header */}
        <div className="flex items-center gap-3 pb-4 border-b mb-4">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isSupport ? "bg-gradient-to-br from-emerald-500 to-teal-600" : "bg-gradient-to-br from-violet-500 to-indigo-600"}`}>
            {isSupport ? <Wrench className="w-5 h-5 text-white" /> : <Sparkles className="w-5 h-5 text-white" />}
          </div>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-gray-800">{isSupport ? "Assistente Tecnico Inteligente" : "Consultor Executivo IA"}</h1>
            <p className="text-xs text-gray-400">{isSupport ? "Diagnostico em tempo real e suporte tecnico" : "Analise de dados em tempo real do seu restaurante"}</p>
          </div>
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
            <button onClick={() => switchMode("support")} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-1.5 ${isSupport ? "bg-white shadow-sm text-emerald-700" : "text-gray-500 hover:text-gray-700"}`} data-testid="mode-support-btn">
              <Wrench className="w-3 h-3" /> Tecnico
            </button>
            <button onClick={() => switchMode("executive")} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-1.5 ${!isSupport ? "bg-white shadow-sm text-violet-700" : "text-gray-500 hover:text-gray-700"}`} data-testid="mode-executive-btn">
              <BarChart3 className="w-3 h-3" /> Executivo
            </button>
          </div>
        </div>

        {/* Diagnostic Health Bar - only in support mode */}
        {isSupport && diagnostics && (
          <div className="mb-3 bg-gray-50 border border-gray-200 rounded-xl p-3" data-testid="diagnostic-panel">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-gray-500" />
                <span className="text-xs font-semibold text-gray-700">Saude do Sistema</span>
              </div>
              <div className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                diagnostics.health_score >= 80 ? "bg-emerald-100 text-emerald-700" :
                diagnostics.health_score >= 60 ? "bg-amber-100 text-amber-700" :
                "bg-red-100 text-red-700"
              }`}>{diagnostics.health_score}% — {diagnostics.health_label}</div>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-1.5">
              {Object.entries(diagnostics.checks || {}).map(([key, val]) => {
                const labels = { configuracao: "Config", menu: "Menu", dominio: "DNS", whatsapp: "WhatsApp", pagamentos: "Pagam.", delivery: "Delivery", assinatura: "Plano", ia: "IA", pedidos: "Pedidos" };
                const icons = { configuracao: Shield, menu: Sparkles, dominio: Globe, whatsapp: Wifi, pagamentos: CreditCard, delivery: Activity, assinatura: CreditCard, ia: Bot, pedidos: BarChart3 };
                const Icon = icons[key] || Activity;
                const OK_STATUSES = new Set(["ok", "ativo", "active", "lifetime", "vitalicio", "configurado", "resolvendo", "conectando"]);
                const WARN_STATUSES = new Set(["parcial", "trial", "past_due", "stale", "qr_necessario"]);
                const isOk = OK_STATUSES.has(val?.status);
                const isPartial = WARN_STATUSES.has(val?.status);
                return (
                  <div key={key} className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-[10px] font-medium ${
                    isOk ? "bg-emerald-50 text-emerald-700" : isPartial ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-600"
                  }`} data-testid={`diag-${key}`}>
                    <Icon className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">{labels[key] || key}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {isSupport && diagLoading && (
          <div className="mb-3 bg-gray-50 border border-gray-200 rounded-xl p-3 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
            <span className="text-xs text-gray-500">Executando diagnostico...</span>
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto space-y-4 pb-4" data-testid="chat-messages">
          {messages.map((msg, i) => (
            <div key={i} className={`flex gap-3 ${msg.role === "user" ? "flex-row-reverse" : ""}`}>
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${msg.role === "user" ? "bg-emerald-100" : isSupport ? "bg-teal-100" : "bg-violet-100"}`}>
                {msg.role === "user" ? <User className="w-4 h-4 text-emerald-600" /> : isSupport ? <Wrench className="w-4 h-4 text-teal-600" /> : <Bot className="w-4 h-4 text-violet-600" />}
              </div>
              <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${msg.role === "user" ? "bg-emerald-600 text-white" : "bg-gray-100 text-gray-800"}`}>
                <div dangerouslySetInnerHTML={{ __html: msg.content.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>").replace(/\n/g, "<br/>") }} />
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex gap-3">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isSupport ? "bg-teal-100" : "bg-violet-100"}`}>
                {isSupport
                  ? <Wrench className="w-4 h-4 text-teal-600 animate-pulse" />
                  : <Bot className="w-4 h-4 text-violet-600" />}
              </div>
              <div className="bg-gray-100 rounded-2xl px-4 py-3 flex items-center gap-2 min-w-[200px]">
                <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400 flex-shrink-0" />
                <span className="text-sm text-gray-500">
                  {isSupport ? SUPPORT_THINKING_STEPS[thinkingStep] : "Analisando dados..."}
                </span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Quick Prompts */}
        {messages.length <= 2 && (
          <div className="flex flex-wrap gap-2 pb-3" data-testid="quick-prompts">
            {QUICK_PROMPTS.map((q, i) => (
              <button
                key={i}
                onClick={() => sendMessage(q)}
                className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-600 px-3 py-1.5 rounded-full transition"
                data-testid={`quick-prompt-${i}`}
              >
                {q}
              </button>
            ))}
          </div>
        )}

        {/* Input */}
        <form
          onSubmit={e => { e.preventDefault(); sendMessage(); }}
          className="flex gap-2 pt-2 border-t"
        >
          <Input
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder={isSupport ? "Descreva o problema tecnico ou pergunte como configurar..." : "Pergunte sobre vendas, produtos, clientes..."}
            className="flex-1 h-11"
            disabled={loading}
            data-testid="chat-input"
          />
          <Button
            type="submit"
            disabled={loading || !input.trim()}
            className="h-11 px-4 bg-violet-600 hover:bg-violet-700 text-white"
            data-testid="chat-send-btn"
          >
            <Send className="w-4 h-4" />
          </Button>
        </form>
      </div>
    </MainLayout>
  );
}
